import { assert } from "../asserts/index.ts";
import type {
  DesignerNodeData,
  ExternalLinkages,
  ExternalNodeContext as ExternalReferencedNodeContext,
  NodeSlotPath,
  VariableConfigs,
} from "../schemas/index.ts";
import {
  decomposeReferenceVariable,
  extractNodeValueRefIds,
  hasNodeReferences,
  isDirectReferenceVariable,
  traverseConditions,
  traverseNodeSubtree,
} from "../shared/index.ts";
import type { GetDesignerNodeDataById } from "../state/index.ts";
import type { GetPortableDesignerNode } from "./getPortableDesignerNode.ts";

interface Options {
  getById: GetDesignerNodeDataById;
  getPortableDesignerNode: GetPortableDesignerNode;
  nodeSlotPathByIdMap: ReadonlyMap<number, NodeSlotPath>;
  getNodeTitleById: (id: number) => string;
}

// node types that dereference other nodes in such a way that they should be included in the export
// for direct adding (aka layoutable node).
const DEREFERENCING_TYPE_IDS = ["layout.slot", "flexbox.container"];

const extractNodeRefIds = (prop: Record<string, number | number[] | undefined>): number[] =>
  Object.values(prop).flatMap((idOrIds) => (Array.isArray(idOrIds) ? idOrIds : idOrIds ? [idOrIds] : []));

const extractReferencedNodes = (node: DesignerNodeData) => {
  const referencedNodes = new Map<number, Set<ExternalReferencedNodeContext>>();

  const addToReferencedNodes = (ids: number | number[], context: ExternalReferencedNodeContext) => {
    for (const id of [ids].flat()) {
      referencedNodes.set(id, new Set([...(referencedNodes.get(id) ?? []), context]));
    }
  };

  if (hasNodeReferences(node.config)) {
    addToReferencedNodes(extractNodeRefIds(node.config.nodeReferences), "reference");
  }

  if ("variables" in node.config && node.config.variables) {
    for (const variable of Object.values(node.config.variables as VariableConfigs)) {
      if (isDirectReferenceVariable(variable)) {
        addToReferencedNodes(decomposeReferenceVariable(variable).nodeId, "variable");
      }
    }
  }

  // Dynamic values keep their target inline in a `node-value` ReferencedValue, so
  // scan the whole config for them.
  addToReferencedNodes(extractNodeValueRefIds(node.config), "value");

  if (node.triggers) {
    for (const { handlers } of node.triggers) {
      for (const { responses } of handlers) {
        for (const { condition: rootCondition, effects } of responses ?? []) {
          if (rootCondition) {
            traverseConditions(rootCondition, (condition) => {
              if (condition.nodeId) {
                addToReferencedNodes(condition.nodeId, "condition");
              }

              if ("refs" in condition.config && condition.config.refs) {
                addToReferencedNodes(extractNodeRefIds(condition.config.refs), "condition");
              }

              addToReferencedNodes(extractNodeValueRefIds(condition.config), "value");
            });
          }

          for (const { nodeId, targetNodeId, config } of effects) {
            addToReferencedNodes(nodeId, "effect");

            if (targetNodeId) {
              addToReferencedNodes(targetNodeId, "effect");
            }

            if ("refs" in config && config.refs) {
              addToReferencedNodes(extractNodeRefIds(config.refs), "effect");
            }

            addToReferencedNodes(extractNodeValueRefIds(config), "value");
          }
        }
      }
    }
  }

  return referencedNodes;
};

export const getPortableNodeLinkagesCreate = ({
  getById,
  getPortableDesignerNode,
  nodeSlotPathByIdMap,
  getNodeTitleById,
}: Options) => {
  const getPortableDereferencedNodes = (
    subjectNode: DesignerNodeData,
    externalReferencedNodes: Map<number, Set<ExternalReferencedNodeContext>>,
  ): ReturnType<GetPortableDesignerNode>[] => {
    assert(subjectNode);
    const nodeIds: number[] = [];
    const dereferencedNodeIds = new Set<number>();

    traverseNodeSubtree(subjectNode, (node) => {
      nodeIds.push(node.id);

      const referencedNodes = extractReferencedNodes(node);

      if (referencedNodes.size === 0) return;

      if (DEREFERENCING_TYPE_IDS.includes(node.typeId)) {
        for (const id of referencedNodes.keys()) {
          dereferencedNodeIds.add(id);
        }
      } else {
        for (const [id, contexts] of referencedNodes.entries()) {
          externalReferencedNodes.set(id, new Set([...(externalReferencedNodes.get(id) ?? []), ...contexts]));
        }
      }
    });

    const externalDereferencedNodeIds = Array.from(dereferencedNodeIds.values()).filter(
      (id) => !nodeIds.some((nid) => nid === id),
    );

    const topLevelDereferenceNodes = externalDereferencedNodeIds.map(getById).map(getPortableDesignerNode);

    const dereferenceNodes = [
      ...topLevelDereferenceNodes,
      ...topLevelDereferenceNodes.flatMap((node) => getPortableDereferencedNodes(node, externalReferencedNodes)),
    ];

    // if in dereference list then we don't want/need them in external paths
    for (const node of dereferenceNodes) {
      externalReferencedNodes.delete(node.id);
    }

    // if in list of nodeIds exported then not external
    for (const externalId of externalReferencedNodes.keys()) {
      if (nodeIds.includes(externalId)) {
        externalReferencedNodes.delete(externalId);
      }
    }

    return dereferenceNodes;
  };

  return (subjectNode: DesignerNodeData): ExternalLinkages | undefined => {
    const externalReferencedNodes = new Map<number, Set<ExternalReferencedNodeContext>>();
    const dereferencedNodes = getPortableDereferencedNodes(subjectNode, externalReferencedNodes);

    if (dereferencedNodes.length === 0 && externalReferencedNodes.size === 0) {
      return undefined;
    }

    const slotPath = nodeSlotPathByIdMap.get(subjectNode.id);
    assert(slotPath);

    const externalNodeSlotPaths: ExternalLinkages["externalNodeSlotPaths"] = [];

    for (const [id, context] of externalReferencedNodes.entries()) {
      const path = nodeSlotPathByIdMap.get(id);
      assert(path, `slotPath not found for referenced node id: ${id}`);

      const typeId = getById(id).typeId;
      const title = getNodeTitleById(id);

      externalNodeSlotPaths.push([id, path, [...context], { title, typeId }]);
    }

    return { slotPath, externalNodeSlotPaths, dereferencedNodes };
  };
};

export type GetPortableNodeLinkages = ReturnType<typeof getPortableNodeLinkagesCreate>;
